import React, {useEffect} from "react";
import Box from "@mui/material/Box";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {GET_SCHEDULES} from "../graphql/queries/gqlGetSchedules";
import {ISchedule} from "../../models/Schedule";
import {IScheduleInfo} from "../../models/IScheduleInfo";
import {GET_SCHEDULE_INFO} from "../graphql/queries/gqlGetScheduleInfo";
import {Button, Checkbox, IconButton, TextField, Typography} from "@mui/material";
import {GenericModal} from "../generic-modal/GenericModal";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {POST_JOB_SCHEDULE} from "../graphql/mutations/gqlPostSchedule";
import {toast} from "react-toastify";

interface IScheduleSelectProps {
	jobId: number;
}

const containerStyle = {
	display: "flex",
	justifyContent: "space-between"
};

const divStyle = {

};

export const ScheduleSelect: React.FC<IScheduleSelectProps> = (props) => {
	const [selectedSchedules, setSelectedSchedules] = React.useState<ISchedule[]>([]);
	const [newSchedule, setNewSchedule] = React.useState<ISchedule>();
	const [scheduleInfos, setScheduleInfos] = React.useState<IScheduleInfo>({nextScheduleDates: []});
	
	const fetchSelectedSchedules = () => {
		graphQLServices.querytoolClient.query({
			query: GET_SCHEDULES,
			variables: {
				"filter": {
					"and": {
						"job": {
							"id": {
								"eq": props.jobId
							}
						}
					}
				}
			},
		})
			.then(result => setSelectedSchedules(result?.data?.schedules?.nodes ?? []));
	};
	
	const addButton = (): React.ReactNode => {
		return (
			<IconButton onClick={() => setNewSchedule({id: 0} as ISchedule)}>
				<AddIcon />
			</IconButton>
		);
	};
	
	const removeButton = (schedule: ISchedule): React.ReactNode => {
		return (
			<IconButton onClick={() => setSelectedSchedules([...selectedSchedules.filter(it => it.id !== schedule.id)])}>
				<RemoveIcon />
			</IconButton>
		);
	};
	
	const fetchSchedulesInfo = () => {
		let schedulesCron = "";
		const enabledSchedules = selectedSchedules.filter(it => it.enabled);
		enabledSchedules.forEach((schedule, index) => {
			schedulesCron += `${schedule.cron}${index !== enabledSchedules.length - 1 ? ";" : ""}`;
		});
		
		if (schedulesCron !== "") {
			graphQLServices.querytoolClient.query({
				query: GET_SCHEDULE_INFO,
				variables: {
					"filter": {
						"cron": schedulesCron,
						"take": 20
					}
				},
			})
				.then(result => {
					setScheduleInfos(result?.data?.scheduleInfo ?? {nextScheduleDates: []});
				})
				.catch(error => {
					setScheduleInfos({nextScheduleDates: [], errors: [{message: error}]});
				});
		} else {
			setScheduleInfos({nextScheduleDates: []});
		}
	};
	
	const onChange = (event: React.ChangeEvent<HTMLInputElement>, schedule: ISchedule) => {
		const tempSchedules = [...selectedSchedules];
		if (event.target.type === "checkbox") {
			tempSchedules[tempSchedules.findIndex(it => it.id === schedule.id)] = {
				...tempSchedules[tempSchedules.findIndex(it => it.id === schedule.id)],
				[event.target.name]: event.target.checked
			};
			
			setSelectedSchedules(tempSchedules);
		} else {
			tempSchedules[tempSchedules.findIndex(it => it.id === schedule.id)] = {
				...tempSchedules[tempSchedules.findIndex(it => it.id === schedule.id)],
				[event.target.name]: event.target.value
			};
			
			setSelectedSchedules(tempSchedules);
		}
	};
	
	const saveChanges = () => {
		selectedSchedules.forEach((schedule, index) => {
			graphQLServices.querytoolClient.mutate({
				mutation: POST_JOB_SCHEDULE,
				variables: {
					scheduleInput: {
						...schedule,
						cronFrom: undefined,
						__typename: undefined,
						job: props.jobId,
					}
				},
			})
				.then(() => toast.success(`Success! ${index + 1}/${selectedSchedules.length}`))
				.catch(() => toast.error("Failed"));
		});
	};
	
	useEffect(() => {
		if (selectedSchedules) {
			fetchSchedulesInfo();
		}
		// eslint-disable-next-line
	}, [selectedSchedules]);
	
	useEffect(() => {
		fetchSelectedSchedules();
		// eslint-disable-next-line
	}, []);
	
	return (
		<>
			{addButton()}
			<div style={containerStyle}>
				<div style={{...divStyle, width: "46%"}}>
					
					{selectedSchedules.map((schedule) => {
						return <Box key={schedule.id} sx={{borderBottom: "1px solid #9c9c9c", marginBottom: "1em"}}>
							<Typography variant={"h6"}>{schedule.description}</Typography>
							<div style={{...containerStyle, alignItems: "center"}}>
								<div style={{...divStyle, height: undefined, flex: 85}}>
									<TextField
										fullWidth
										label="Cron"
										name="cron"
										value={schedule.cron}
										margin="normal"
										type="text"
										required
										onChange={event => onChange(event as React.ChangeEvent<HTMLInputElement>, schedule)}
									/>
								</div>
								<div style={{...divStyle, height: undefined, flex: 15}}>
									<Checkbox
										name="enabled"
										value={schedule.enabled}
										checked={schedule.enabled}
										onChange={event => onChange(event, schedule)}
										required />
								</div>
								{removeButton(schedule)}
							</div>
						</Box>;
					})}
				</div>
				<div style={{...divStyle, width: "46%"}}>
					{(scheduleInfos.errors?.length ?? 0) > 0 && <><Typography variant={"h6"}> Errors</Typography>
						{(scheduleInfos.errors ?? []).map(it => <div>{`${it.message}`}</div>)}
					</>}
					
					{scheduleInfos.nextScheduleDates?.length > 0 && <Typography variant={"h6"}> Next 20 Runs</Typography>}
					{(scheduleInfos.nextScheduleDates ?? []).map(it => <div>{it}</div>)}
				</div>
			</div>
			<Button onClick={saveChanges}>Save Changes</Button>
			<GenericModal open={!!newSchedule} title={"New Schedule"} onClose={() => setNewSchedule(undefined)}>
				<Box width={"400px"} key={"new-item-schedule"}>
					<TextField
						fullWidth
						label="Title"
						name="title"
						margin="normal"
						type="text"
						required
						onChange={event => setNewSchedule({...newSchedule as any, description: event.currentTarget.value})}
					/>
					<div style={{...containerStyle, alignItems: "center"}}>
						<div style={{...divStyle, height: undefined, flex: 85}}>
							<TextField
								fullWidth
								label="Cron"
								name="cron"
								margin="normal"
								type="text"
								required
								onChange={event => setNewSchedule({...newSchedule as any, cron: event.currentTarget.value})}
							/>
						</div>
						<div style={{...divStyle, height: undefined, flex: 15}}>
							<Checkbox
								name="enabled"
								value={true}
								checked={true}
								onChange={event => setNewSchedule({...newSchedule as any, enabled: event.currentTarget.checked})}
								required />
						</div>
					</div>
					<Button variant={"outlined"} onClick={() => {
						setSelectedSchedules([...selectedSchedules, newSchedule as ISchedule]);
						setNewSchedule(undefined);
					}
					}>Add</Button>
				</Box>
			</GenericModal>
		</>
	);
};
